import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  whatsappTemplatesService,
} from '../services/whatsappTemplates.service';
import {
  Template,
  ListTemplatesParams,
  UseTemplatesOptions,
  UseTemplateOptions,
  ListTemplatesResponse,
  TemplateResponse,
  CreateTemplateResponse,
  UpdateTemplateResponse,
  DeleteTemplateResponse,
  SubmitTemplateResponse,
  SyncTemplateResponse,
  UploadMediaResponse,
  MetaTemplatesResponse,
  ImportTemplatesResponse,
  SyncAllTemplatesResponse,
  TemplateStatsResponse,
} from '../types/whatsappTemplates.types';

// Query Keys
export const WHATSAPP_TEMPLATES_QUERY_KEYS = {
  all: ['whatsappTemplates'] as const,
  lists: () => [...WHATSAPP_TEMPLATES_QUERY_KEYS.all, 'list'] as const,
  list: (chatBotId: string, params?: ListTemplatesParams) =>
    [...WHATSAPP_TEMPLATES_QUERY_KEYS.lists(), chatBotId, params] as const,
  details: () => [...WHATSAPP_TEMPLATES_QUERY_KEYS.all, 'detail'] as const,
  detail: (templateId: string) =>
    [...WHATSAPP_TEMPLATES_QUERY_KEYS.details(), templateId] as const,
  meta: () => [...WHATSAPP_TEMPLATES_QUERY_KEYS.all, 'meta'] as const,
  metaTemplates: (chatBotId: string) =>
    [...WHATSAPP_TEMPLATES_QUERY_KEYS.meta(), chatBotId] as const,
  stats: (chatBotId: string) =>
    [...WHATSAPP_TEMPLATES_QUERY_KEYS.all, 'stats', chatBotId] as const,
};

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Hook to fetch list of WhatsApp templates
 */
export const useTemplates = (
  chatBotId: string | null,
  params: ListTemplatesParams = {},
  options: UseTemplatesOptions = {}
) => {
  const { enabled = true, refetchInterval, ...queryOptions } = options;

  return useQuery<ListTemplatesResponse, Error>({
    queryKey: WHATSAPP_TEMPLATES_QUERY_KEYS.list(chatBotId!, params),
    queryFn: () => whatsappTemplatesService.listTemplates(chatBotId!, params),
    enabled: Boolean(chatBotId) && enabled,
    refetchInterval,
    ...queryOptions,
  });
};

/**
 * Hook to fetch a single WhatsApp template
 */
export const useTemplate = (
  templateId: string | null,
  options: UseTemplateOptions = {}
) => {
  const { enabled = true, ...queryOptions } = options;

  return useQuery<TemplateResponse, Error>({
    queryKey: WHATSAPP_TEMPLATES_QUERY_KEYS.detail(templateId!),
    queryFn: () => whatsappTemplatesService.getTemplate(templateId!),
    enabled: Boolean(templateId) && enabled,
    ...queryOptions,
  });
};

/**
 * Hook to fetch Meta templates for a chatbot
 */
export const useMetaTemplates = (
  chatBotId: string | null,
  options: { enabled?: boolean; staleTime?: number } = {}
) => {
  const { enabled = true } = options;

  return useQuery<MetaTemplatesResponse, Error>({
    queryKey: WHATSAPP_TEMPLATES_QUERY_KEYS.metaTemplates(chatBotId!),
    queryFn: () => whatsappTemplatesService.getMetaTemplates(chatBotId!),
    enabled: Boolean(chatBotId) && enabled,
    retry: 2,
  });
};

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Hook to create a new WhatsApp template
 */
export const useCreateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation<
    CreateTemplateResponse,
    Error,
    { chatBotId: string; data: any; status?: 'draft' | 'submit' }
  >({
    mutationFn: ({ chatBotId, data, status }) =>
      whatsappTemplatesService.createTemplate(chatBotId, data, status),
    onSuccess: (response) => {
      // Invalidate and refetch all template-related queries so counts and lists stay in sync
      queryClient.invalidateQueries({
        queryKey: WHATSAPP_TEMPLATES_QUERY_KEYS.all,
      });
      queryClient.invalidateQueries({
        queryKey: ['dashboardCounts'],
      });

      toast.success(response.message);
    }
  });
};


/**
 * Hook to update a WhatsApp template
 */
export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateTemplateResponse,
    Error,
    { templateId: string; data: any; status?: 'draft' | 'submit' }
  >({
    mutationFn: ({ templateId, data, status }) =>
      whatsappTemplatesService.updateTemplate(templateId, data, status),
    onSuccess: (response) => {
      // Refresh all template-related queries so counts and lists stay consistent
      queryClient.invalidateQueries({
        queryKey: WHATSAPP_TEMPLATES_QUERY_KEYS.all,
      });
      queryClient.invalidateQueries({
        queryKey: ['dashboardCounts'],
      });

      toast.success(response.message || 'Template updated successfully');
    },
  });
};

/**
 * Hook to delete a WhatsApp template
 */
export const useDeleteTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation<DeleteTemplateResponse, Error, { templateId: string; force?: boolean }>({
    mutationFn: ({ templateId, force }) => whatsappTemplatesService.deleteTemplate(templateId, force),
    onSuccess: (response, { templateId }) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: WHATSAPP_TEMPLATES_QUERY_KEYS.detail(templateId),
      });

      // Refresh all template-related queries so counts and lists stay in sync
      queryClient.invalidateQueries({
        queryKey: WHATSAPP_TEMPLATES_QUERY_KEYS.all,
      });
      queryClient.invalidateQueries({
        queryKey: ['dashboardCounts'],
      });

      toast.success(response.message || 'Template deleted successfully');
    }
  });
};

/**
 * Hook to submit template to Meta for approval
 */
export const useSubmitTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation<SubmitTemplateResponse, Error, string>({
    mutationFn: (templateId) => whatsappTemplatesService.submitTemplate(templateId),
    onSuccess: (response) => {
      // Refresh all template-related queries so counts and lists stay in sync
      queryClient.invalidateQueries({
        queryKey: WHATSAPP_TEMPLATES_QUERY_KEYS.all,
      });
      queryClient.invalidateQueries({
        queryKey: ['dashboardCounts'],
      });

      toast.success(response.message || 'Template submitted to Meta for approval');
    }
  });
};

/**
 * Hook to sync template status from Meta
 */
export const useSyncTemplateStatus = () => {
  const queryClient = useQueryClient();

  return useMutation<SyncTemplateResponse, Error, string>({
    mutationFn: (templateId) => whatsappTemplatesService.syncTemplateStatus(templateId),
    onSuccess: (response, templateId) => {
      // Update the specific template detail cache with the resolved template payload
      queryClient.setQueryData(
        WHATSAPP_TEMPLATES_QUERY_KEYS.detail(templateId),
        {
          success: response.success,
          message: response.message,
          data: response.data.template,
        }
      );

      // Refresh all template-related queries so counts and lists stay in sync
      queryClient.invalidateQueries({
        queryKey: WHATSAPP_TEMPLATES_QUERY_KEYS.all,
      });
      queryClient.invalidateQueries({
        queryKey: ['dashboardCounts'],
      });

      toast.success(response.message || 'Template status synced successfully');
    }
  });
};

/**
 * Hook to delete template from Meta and local database
 */
export const useDeleteTemplateFromMeta = () => {
  const queryClient = useQueryClient();

  return useMutation<DeleteTemplateResponse, Error, string>({
    mutationFn: (templateId) =>
      whatsappTemplatesService.deleteTemplateFromMeta(templateId),
    onSuccess: (response, templateId) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: WHATSAPP_TEMPLATES_QUERY_KEYS.detail(templateId),
      });

      // Refresh all template-related queries and Meta templates
      queryClient.invalidateQueries({
        queryKey: WHATSAPP_TEMPLATES_QUERY_KEYS.all,
      });
      queryClient.invalidateQueries({
        queryKey: WHATSAPP_TEMPLATES_QUERY_KEYS.meta(),
      });
      queryClient.invalidateQueries({
        queryKey: ['dashboardCounts'],
      });

      toast.success(response.message || 'Template deleted from Meta successfully');
    }
  });
};

/**
 * Hook to upload media file directly to Meta
 * Uploads file and returns media ID that can be used in templates
 */
export const useUploadTemplateMedia = () => {
  return useMutation<
    UploadMediaResponse,
    Error,
    { chatBotId: string; file: File | null; type: 'image' | 'video' | 'document'; mediaUrl?: string }
  >({
    mutationFn: ({ chatBotId, file, type, mediaUrl }) =>
      whatsappTemplatesService.uploadTemplateMedia(chatBotId, file, type, mediaUrl),
    onSuccess: (response) => {
      toast.success(response.message || 'Media uploaded to Meta successfully!');
    }
  });
};

/**
 * Hook to import templates from Meta to database
 */
export const useImportTemplatesFromMeta = () => {
  const queryClient = useQueryClient();

  return useMutation<ImportTemplatesResponse, Error, string>({
    mutationFn: (chatBotId) => whatsappTemplatesService.importTemplatesFromMeta(chatBotId),
    onSuccess: (response) => {
      // Invalidate all template-related queries to refresh data
      queryClient.invalidateQueries({
        queryKey: WHATSAPP_TEMPLATES_QUERY_KEYS.all,
      });
      queryClient.invalidateQueries({
        queryKey: ['dashboardCounts'],
      });
      toast.success(response.message)
    }
  });
};

/**
 * Hook to sync all submitted templates status from Meta
 */
export const useSyncAllTemplates = () => {
  const queryClient = useQueryClient();

  return useMutation<SyncAllTemplatesResponse, Error, string>({
    mutationFn: (chatBotId) => whatsappTemplatesService.syncAllTemplates(chatBotId),
    onSuccess: (response) => {
      // Invalidate all template-related queries to refresh data
      queryClient.invalidateQueries({
        queryKey: WHATSAPP_TEMPLATES_QUERY_KEYS.all,
      });
      queryClient.invalidateQueries({
        queryKey: ['dashboardCounts'],
      });

      toast.success(
        response.message ||
        `Successfully synced ${response.data.syncedCount} templates`
      );
    }
  });
};

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Hook to get template statistics using dedicated stats endpoint
 * More efficient than fetching all templates
 */
export const useTemplateStats = (chatBotId: string | null) => {
  const { data: statsResponse, isLoading } = useQuery<TemplateStatsResponse, Error>({
    queryKey: WHATSAPP_TEMPLATES_QUERY_KEYS.stats(chatBotId!),
    queryFn: () => whatsappTemplatesService.getTemplateStats(chatBotId!),
    enabled: Boolean(chatBotId),
  });

  // Default stats if no data
  const stats = statsResponse?.data || {
    total: 0,
    draft: 0,
    submitted: 0,
    approved: 0,
    rejected: 0,
    disabled: 0,
  };

  return {
    stats,
    isLoading,
  };
};

/**
 * Hook to manage rejected templates with enhanced functionality
 * Note: Uses page=1&limit=100 to get all templates (backend max is 100)
 */
export const useRejectedTemplates = (chatBotId: string | null) => {
  const { data: templatesResponse, isLoading } = useTemplates(chatBotId, {
    page: 1,
    limit: 100,
  });

  // Handle both new paginated format and old array format
  const templates = templatesResponse?.data?.templates ||
    (Array.isArray(templatesResponse?.data) ? templatesResponse.data : []) || [];

  // Filter rejected templates
  const rejectedTemplates = templates.filter(
    template => template.status === 'rejected'
  ) || [];

  // Get most recent rejection
  const latestRejection = rejectedTemplates.length > 0
    ? rejectedTemplates.sort((a, b) =>
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    )[0]
    : null;

  // Check if there are new rejections (templates rejected in the last 24 hours)
  const recentRejections = rejectedTemplates.filter(template => {
    const updateTime = new Date(template.updatedAt);
    const dayAgo = new Date();
    dayAgo.setHours(dayAgo.getHours() - 24);
    return updateTime > dayAgo;
  });

  // Common rejection reasons analysis
  const rejectionReasons = rejectedTemplates.reduce((acc, template) => {
    if (template.rejectionReason) {
      const reason = template.rejectionReason.toLowerCase();
      acc[reason] = (acc[reason] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);

  const mostCommonRejectionReason = Object.entries(rejectionReasons)
    .sort(([, a], [, b]) => (b as number) - (a as number))[0];

  return {
    rejectedTemplates,
    rejectedCount: rejectedTemplates.length,
    latestRejection,
    recentRejections,
    recentRejectionsCount: recentRejections.length,
    rejectionReasons,
    mostCommonRejectionReason: mostCommonRejectionReason
      ? { reason: mostCommonRejectionReason[0], count: mostCommonRejectionReason[1] }
      : null,
    isLoading,
    hasRejections: rejectedTemplates.length > 0,
    hasRecentRejections: recentRejections.length > 0,
  };
};

/**
 * Hook for optimistic updates
 * Useful when you want immediate UI feedback
 */
export const useOptimisticTemplateUpdate = () => {
  const queryClient = useQueryClient();

  const updateTemplateOptimistically = (
    templateId: string,
    updates: Partial<Template>
  ) => {
    // Update detail query
    queryClient.setQueryData(
      WHATSAPP_TEMPLATES_QUERY_KEYS.detail(templateId),
      (old: TemplateResponse | undefined) => {
        if (!old) return old;
        return {
          ...old,
          data: { ...old.data, ...updates },
        };
      }
    );

    // Update in list queries
    queryClient.setQueriesData(
      { queryKey: WHATSAPP_TEMPLATES_QUERY_KEYS.lists() },
      (old: ListTemplatesResponse | undefined) => {
        if (!old || !old.data?.templates) return old;
        return {
          ...old,
          data: {
            ...old.data,
            templates: old.data.templates.map((template) =>
              template._id === templateId ? { ...template, ...updates } : template
            ),
          },
        };
      }
    );
  };

  return { updateTemplateOptimistically };
};
